const zero = () => ({ inputTokens: 0, outputTokens: 0, cachedInputTokens: 0 });
/**
 * Each node's own usage: its reported usage minus its direct children's. The engine folds a
 * sub-agent's usage into its parent before reporting the parent, so summing raw node usage counts
 * nested agents twice, and pricing a parent at its own rate would bill its children's tokens too.
 */
export function exclusiveUsageByNode(nodes) {
    const childTotals = new Map();
    for (const node of Object.values(nodes)) {
        if (!node.parentNodeId || !node.usage)
            continue;
        const acc = childTotals.get(node.parentNodeId) ?? zero();
        acc.inputTokens += node.usage.inputTokens ?? 0;
        acc.outputTokens += node.usage.outputTokens ?? 0;
        acc.cachedInputTokens += node.usage.cachedInputTokens ?? 0;
        childTotals.set(node.parentNodeId, acc);
    }
    const exclusive = new Map();
    for (const node of Object.values(nodes)) {
        if (!node.usage)
            continue;
        const child = childTotals.get(node.nodeId) ?? zero();
        exclusive.set(node.nodeId, {
            inputTokens: Math.max(0, (node.usage.inputTokens ?? 0) - child.inputTokens),
            outputTokens: Math.max(0, (node.usage.outputTokens ?? 0) - child.outputTokens),
            cachedInputTokens: Math.max(0, (node.usage.cachedInputTokens ?? 0) - child.cachedInputTokens),
        });
    }
    return exclusive;
}
/**
 * USD for one node's own tokens. Cached input bills at `cachedInput` when the model has that rate
 * and at the full input rate otherwise, so an unknown cache discount overstates rather than hides.
 */
export function nodeCostUsd(usage, pricing) {
    if (!usage || !pricing)
        return undefined;
    const input = usage.inputTokens ?? 0;
    const cached = Math.min(input, usage.cachedInputTokens ?? 0);
    const cachedRate = pricing.cachedInput ?? pricing.input;
    return (((input - cached) * pricing.input +
        cached * cachedRate +
        (usage.outputTokens ?? 0) * pricing.output) /
        1_000_000);
}
export function selectRunSummary(state, runId, pricingFor) {
    const run = state.runs[runId];
    if (!run)
        return undefined;
    const nodes = Object.values(state.nodes).filter((n) => n.runId === runId);
    const own = exclusiveUsageByNode(Object.fromEntries(nodes.map((n) => [n.nodeId, n])));
    const usage = zero();
    let costUsd;
    for (const node of nodes) {
        const nodeUsage = own.get(node.nodeId);
        if (!nodeUsage)
            continue;
        usage.inputTokens += nodeUsage.inputTokens;
        usage.outputTokens += nodeUsage.outputTokens;
        usage.cachedInputTokens += nodeUsage.cachedInputTokens;
        const cost = node.model ? nodeCostUsd(nodeUsage, pricingFor?.(node.model)) : undefined;
        if (cost !== undefined)
            costUsd = (costUsd ?? 0) + cost;
    }
    return {
        runId,
        status: run.status,
        rootAgentId: run.rootAgentId,
        rootName: nodes.find((n) => n.parentNodeId === null)?.name,
        agentCount: nodes.length,
        handoffCount: nodes.filter((n) => n.kind === 'handoff').length,
        durationMs: run.endedAt === null ? null : run.endedAt - run.startedAt,
        usage,
        costUsd,
        error: run.error,
    };
}
/** Nodes still running, as a set of their agent ids, for highlighting them on a canvas. */
export function selectRunningAgentIds(state) {
    const running = new Set();
    for (const node of Object.values(state.nodes)) {
        if (node.status === 'running')
            running.add(node.agentId);
    }
    return running;
}
/** Adaptive-precision USD: "$0.0042", "$0.031", "$1.23". */
export function formatCostUsd(usd) {
    if (usd <= 0)
        return '$0';
    if (usd < 0.01)
        return `$${usd.toFixed(4)}`;
    if (usd < 1)
        return `$${usd.toFixed(3)}`;
    return `$${usd.toFixed(2)}`;
}
